import { useEffect, useState } from 'react';

// web-rnw twin of ./devicePreference.ts (see that file's header) — same
// per-device boolean preference factory, localStorage instead of
// AsyncStorage. Resolved instead of the bare file by Vite's `.web.ts`
// extension preference.
export function createBooleanDevicePreference(key: string, defaultValue: boolean) {
  let snapshot = defaultValue;
  const listeners = new Set<(enabled: boolean) => void>();

  const get = (): boolean => snapshot;

  const load = async (): Promise<boolean> => {
    try {
      const raw = localStorage.getItem(key);
      snapshot = raw === null ? defaultValue : raw === '1';
    } catch {
      // private mode / unavailable — keep the current snapshot.
    }
    return snapshot;
  };

  const persist = async (enabled: boolean): Promise<void> => {
    snapshot = enabled;
    listeners.forEach((fn) => fn(enabled));
    try {
      localStorage.setItem(key, enabled ? '1' : '0');
    } catch {
      // Snapshot already updated; persistence retries on next change.
    }
  };

  function usePreference(): {
    enabled: boolean;
    setEnabled: (v: boolean) => void;
  } {
    const [enabled, setEnabledState] = useState(snapshot);
    useEffect(() => {
      let cancelled = false;
      const listener = (v: boolean) => setEnabledState(v);
      listeners.add(listener);
      void load().then((next) => {
        if (!cancelled) setEnabledState(next);
      });
      return () => {
        cancelled = true;
        listeners.delete(listener);
      };
    }, []);
    return { enabled, setEnabled: (v) => void persist(v) };
  }

  return { get, load, persist, usePreference };
}
